import { HttpClient, HttpErrorResponse, HttpEventType, HttpHeaders, HttpParams, HttpProgressEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { logDiagnostic, logRequest } from '@office-start/telemetry-service';
import { KnownRequestInstrumentationEvents, TelemetryProperties } from '@office-start/telemetry-types';
import { timeout } from 'rxjs/operators';

import { ServiceWorkerConfigService } from '../../service-worker/service-worker-config.service';
import { serviceWorkerFetchStrategyHeader } from '../../service-worker/service-worker-fetch-strategy-header.constant';
import { ServiceWorkerState, ServiceWorkerStateService } from '../../service-worker/service-worker-state.service';
import { HttpStatusCode } from '../constants/http-status-code.enum';
import { WorkloadQueryParameter } from '../constants/workload-query-parameter.enum';
import { WorkloadQueryParameterToken } from '../constants/workload-query-parameter.injection-token';
import { HttpRequestMethod } from '../models/http-request-method.enum';
import { HttpRequestOptions } from '../models/http-request-options.model';
import { OfficeHomeApiConfig, OfficeHomeApiConfigService } from '../services/officehome-api-config.service';
import { Performance } from '../services/performance.service';
import { WindowService } from '../services/window.service';
import { ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { ContextService } from '../shared-context/context.service';
import { PromiseCancellationToken } from '../utilities/promise-cancellation-token.utility';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents, PiiString } from './instrumentation.service';

// These are the fetch startegies supported by our Service Worker.
// These can be specified as value of X-SW-OHP-FetchStrategy header to instruct Service Worker to use that strategy when handling request.
export const enum FetchStrategy {
    CacheOnly = 'cache-only',
    NetworkFirst = 'network-first',
    NetworkOnly = 'network-only'
}

export interface InstrumentedHttpDataResponse<T> {
    data: T;
    status: number;
    subStatus?: number;
    headers: HttpHeaders;
    config: HttpRequestOptions;
    ok: boolean;
    statusText: string;
    fromCache: boolean;
}

/**
 * Interface defining the type of callback function that can be specified as parameter
 * for instrument() method in InstrumentedHttpPromise<T>.
 */
export interface InstrumentedHttpPromiseCallback<T> {
    /**
     * The signature of the callback function will be called before instrumentation event is recorded.
     *
     * @param result The result of instrumented Http operation.
     * @param params The additional instrumentation params that will be recorded. Except 'HttpCode' (InstrumentationParam.HttpCode), please don't override other property in request event.
     */
    (result: InstrumentedHttpDataResponse<T>, params: InstrumentationParams | TelemetryProperties): void;
}

/**
 * Class defining the type of promise object returned by instrumented Http methods.
 */
export class InstrumentedHttpPromise<T> implements Promise<InstrumentedHttpDataResponse<T>> {
    public instrumentCallback: InstrumentedHttpPromiseCallback<T>;

    constructor(public promise: Promise<InstrumentedHttpDataResponse<T>>) {
    }

    /*
     * This is needed so that InstrumentedHttpPromise will conform to the Promise interface.
     * A compilation error ocurred when changing from `core-js` typings to TypeScript `tsconfig.json` option `lib: [ "es2017" ]` without this property.
     * This changes: instrumentedPromise.toString() from '[object Object]' to '[object Promise]'
     * For more information: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Symbol/toStringTag
     */
    public get [Symbol.toStringTag](): 'Promise' {
        return 'Promise';
    }

    /**
     * This method allows for specifying callback function that will be called when Http operation
     * is ready to be instrumented. The callback will get the http response object along with
     * instrumentation event object.
     * This is useful when you want to augment instrumentation event object before it is recorded.
     * The most common case for this is when you need to log additional parameters extracted from response data.
     * MUST be used before a .then() or a .catch()
     *
     * @param callback The function that will be called to allow for modifying instrumentation event data.
     */
    public instrument(callback: InstrumentedHttpPromiseCallback<T>): Promise<InstrumentedHttpDataResponse<T>> {
        this.instrumentCallback = callback;
        return this;
    }

    public then(onfulfilled: ((value: InstrumentedHttpDataResponse<T>) => any), onrejected?: ((reason: InstrumentedHttpDataResponse<T>) => any)): Promise<any> {
        return this.promise.then(onfulfilled, onrejected);
    }

    public catch(onrejected: (reason: InstrumentedHttpDataResponse<T>) => any): Promise<any> {
        return this.promise.catch(onrejected);
    }

    public finally(onfinally?: (() => void) | undefined | null): Promise<InstrumentedHttpDataResponse<T>> {
        return this.promise.finally(onfinally);
    }
}

/**
 * Service for executing instrumented Http operations.
 */
@Injectable({
    providedIn: 'root'
})
export class InstrumentedHttp {
    private static readonly unknownError = 'NotAvailable';
    private static readonly unknownStatusCode = -1;
    private readonly officeHomeApiConfig: OfficeHomeApiConfig;

    constructor(
        private readonly httpClient: HttpClient,
        private readonly contextService: ContextService,
        private readonly startupConfigService: ClientStartupConfigService,
        private readonly instrumentationService: InstrumentationService,
        private readonly windowService: WindowService,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        private readonly serviceWorkerStateService: ServiceWorkerStateService,
        private readonly serviceWorkerConfigService: ServiceWorkerConfigService,
        @Inject(WorkloadQueryParameterToken) private readonly workloadQueryParameterValue: WorkloadQueryParameter
    ) {
        this.officeHomeApiConfig = officeHomeApiConfigService.config;
    }

    /**
     * Runs and instruments the result of execution of the given Http operation.
     *
     * @param eventName Name of the instrumentation event.
     * @param otelEventName Name of the OTEL instrumentation event.
     * @param requestConfig The object which describes Http operation to run.
     * @param strategy Fetch stategy used when executing request.
     * @param onUploadProgress A callback to receive upload progress events.
     * @param onDownloadProgress A callback to receive download progress events.
     * @returns Promise object that will be completed asynchronously when Http operation is done
     * and its result is instrumented.
     */
    public request<T>(
        eventName: KnownInstrumentationEvents,
        otelEventName: KnownRequestInstrumentationEvents,
        requestConfig: HttpRequestOptions,
        strategy: FetchStrategy,
        onUploadProgress?: (progressEvent: HttpProgressEvent) => void,
        onDownloadProgress?: (progressEvent: HttpProgressEvent) => void,
        cancellationToken?: PromiseCancellationToken,
        timeoutInMs?: number): InstrumentedHttpPromise<T> {

        // Start measuring the latency
        const start = Performance.now();
        let latency = 0;

        const fromCache = strategy === FetchStrategy.CacheOnly;

        const responsePromise = new Promise<InstrumentedHttpDataResponse<T>>((resolve, reject) => {
            if (fromCache) {
                const serviceWorkerEnabled = this.serviceWorkerConfigService.config.enabled;
                // Cache is supported only when ServiceWorker is enabled and available to handle fetches
                if (!serviceWorkerEnabled || this.serviceWorkerStateService.currentState !== ServiceWorkerState.Controlled) {
                    const error = `Cache is not available. Service Worker is in ${serviceWorkerEnabled ? this.serviceWorkerStateService.currentState : 'disabled'} state`;
                    reject(InstrumentedHttp.failedResponseWithCode<T>(HttpStatusCode.NotImplemented, requestConfig, fromCache, error));
                    return;
                }
            }

            this.prepareRequest(requestConfig, strategy);

            const headersAsObject = {};
            if (requestConfig.headers) {
                requestConfig.headers.forEach((value, key) => {
                    headersAsObject[key] = value;
                });
            }

            const reportProgress = !!onUploadProgress || !!onDownloadProgress;
            const observe = reportProgress ? 'events' : 'response';

            const httpRequestInitOptions = {
                headers: new HttpHeaders(headersAsObject),
                params: new HttpParams({ fromObject: requestConfig.params }),
                method: requestConfig.method,
                reportProgress,
                responseType: requestConfig.responseType,
                observe
            };

            const request = new HttpRequest(requestConfig.method, requestConfig.url, requestConfig.body, httpRequestInitOptions);

            const mapResponse = (response: HttpResponse<T> | HttpErrorResponse) => {
                let data;
                if (response instanceof HttpResponse) {
                    data = response.body;
                } else if (response instanceof HttpErrorResponse) {
                    data = response.error;
                }

                return {
                    data,
                    status: response.status,
                    headers: response.headers,
                    config: requestConfig,
                    ok: response.ok,
                    statusText: response.statusText,
                    fromCache
                } as InstrumentedHttpDataResponse<T>;
            };

            const subscription = this.httpClient
                .request<T>(request).pipe(
                    timeout(timeoutInMs || this.officeHomeApiConfig.defaultTimeoutInMs))
                .subscribe(
                    event => {
                        if (event instanceof HttpResponse) {
                            resolve(mapResponse(event));
                        } else if (event.type === HttpEventType.UploadProgress && onUploadProgress) {
                            onUploadProgress(event);
                        } else if (event.type === HttpEventType.DownloadProgress && onDownloadProgress) {
                            onDownloadProgress(event);
                        }
                    },
                    error => {
                        if (error.name === 'TimeoutError') {
                            reject(InstrumentedHttp.failedResponseWithCode<T>(HttpStatusCode.Timeout, requestConfig, fromCache));
                            return;
                        }

                        const navigator = this.windowService.nativeWindow.navigator;
                        if (navigator && typeof (navigator.onLine) === 'boolean' && !navigator.onLine) {
                            reject(InstrumentedHttp.failedResponseWithCode<T>(HttpStatusCode.Offline, requestConfig, fromCache));
                            return;
                        }
                        reject(mapResponse(error));
                    });
            if (cancellationToken) {
                cancellationToken.onCancel(() => subscription.unsubscribe());
            }
        });

        // Construct promise object that will be returned by instrumented operation.
        // It combines result of Http operation with instrumented data
        const instrumentedPromise = new InstrumentedHttpPromise<T>(responsePromise);

        // Instrument response
        const instrumentResponse = (instrumentedHttpDataResponse: InstrumentedHttpDataResponse<T>) => {
            // Do not instrument cache failure when cache is not supported
            if (instrumentedHttpDataResponse.fromCache && !instrumentedHttpDataResponse.ok && instrumentedHttpDataResponse.status === HttpStatusCode.NotImplemented) {
                return;
            }

            latency = Math.ceil(Performance.now() - start);

            // NOTE: Angular documentation indicates that status and error may not be returned on all browsers
            let status = instrumentedHttpDataResponse.status;
            if (status === null || typeof status === 'undefined') {
                status = InstrumentedHttp.unknownStatusCode;
            }

            const statusText = instrumentedHttpDataResponse.statusText;

            let error: string | PiiString;
            let errorText = statusText;
            if (statusText === null || typeof statusText === 'undefined') {
                error = InstrumentedHttp.unknownError;
                errorText = InstrumentedHttp.unknownError;
            } else if (statusText.toLowerCase() === 'ok') {
                error = statusText;
            } else {
                error = new PiiString(statusText);
            }

            const instrumentationParams = {};

            // Post-process event before recording
            if (instrumentedPromise.instrumentCallback) {
                instrumentedPromise.instrumentCallback(instrumentedHttpDataResponse, instrumentationParams);
            }

            // Check if there is property override of httpCode
            if (instrumentationParams.hasOwnProperty(InstrumentationParam.HttpCode)) {
                status = instrumentationParams[InstrumentationParam.HttpCode];
                delete instrumentationParams[InstrumentationParam.HttpCode];
            }
            logRequest({
                eventName: otelEventName,
                error: errorText,
                latency,
                fromCache: instrumentedHttpDataResponse.fromCache,
                httpCode: status
            });
            // log custom fields to OTEL Diagnostic events
            if (Object.keys(instrumentationParams).length !== 0) {
                logDiagnostic({ eventName: otelEventName, params: instrumentationParams });
            }

            // TODO: Remove following code once we migrate to OTEL
            const instrumentationEvent = this.instrumentationService.generateRequestEvent(
                eventName,
                status,
                latency,
                error,
                instrumentedHttpDataResponse.fromCache,
                instrumentationParams);
            instrumentationEvent.record();
        };

        // Necessary to instrument the cancelled http request, since the promise never resolves
        const cancelTask = cancellationToken ?
            cancellationToken.onCancel(() => Promise.resolve(InstrumentedHttp.failedResponseWithCode<T>(HttpStatusCode.Cancelled, requestConfig, fromCache))) :
            new Promise(() => { /*Never resolve*/ });
        Promise.race([responsePromise, cancelTask]).then(instrumentResponse, instrumentResponse);

        return instrumentedPromise;
    }

    /**
     * Runs and instruments the result of execution of Http GET operation.
     *
     * @param eventName Name of the instrumentation event.
     * @param otelEventName Name of the OTEL instrumentation event.
     * @param url Absolute or relative URL of the resource that is being requested.
     * @param requestConfig The object which provides configuration for Http GET.
     * @param strategy Fetch stategy used when executing request.
     * @param onDownloadProgress A callback to receive download progress events.
     * @param cancellationToken A cancellation token used to cancel the request.
     * @param timeoutInMs Maximum time to wait on the response.
     * @returns Promise object that will be completed asynchronously when Http GET is done
     * and its result is instrumented.
     */
    public get<T>(
        eventName: KnownInstrumentationEvents,
        otelEventName: KnownRequestInstrumentationEvents,
        url: string,
        requestConfig: HttpRequestOptions,
        strategy: FetchStrategy,
        onDownloadProgress?: (progressEvent: HttpProgressEvent) => void,
        cancellationToken?: PromiseCancellationToken,
        timeoutInMs?: number): InstrumentedHttpPromise<T> {

        const getRequestConfig: HttpRequestOptions = { ...requestConfig };
        getRequestConfig.url = url;
        getRequestConfig.method = HttpRequestMethod.Get;
        return this.request<T>(eventName, otelEventName, getRequestConfig, strategy, null, onDownloadProgress, cancellationToken, timeoutInMs);
    }

    /**
     * Runs and instruments the result of execution of Http POST operation.
     *
     * @param eventName Name of the instrumentation event
     * @param otelEventName Name of the OTEL instrumentation event.
     * @param url Absolute or relative URL of the resource that is being updated
     * @param data Data to be sent as the request message data
     * @param requestConfig The object which provide configuration for Http POST
     * @param onUploadProgress A callback to receive upload progress events.
     * @param onDownloadProgress A callback to receive download progress events.
     * @param cancellationToken A cancellation token used to cancel the request.
     * @param timeoutInMs Maximum time to wait on the response.
     * @returns Promise object that will be completed asynchronously when Http POST is done
     * and its result is instrumented.
     */
    public post<T>(
        eventName: KnownInstrumentationEvents,
        otelEventName: KnownRequestInstrumentationEvents,
        url: string,
        data: any,
        requestConfig: HttpRequestOptions = {},
        onUploadProgress?: (progressEvent: HttpProgressEvent) => void,
        onDownloadProgress?: (progressEvent: HttpProgressEvent) => void,
        cancellationToken?: PromiseCancellationToken,
        timeoutInMs?: number,
        strategy?: FetchStrategy): InstrumentedHttpPromise<T> {

        const postRequestConfig: HttpRequestOptions = { ...requestConfig };
        postRequestConfig.url = url;
        postRequestConfig.method = HttpRequestMethod.Post;
        postRequestConfig.body = data;
        const postStrategy = strategy ? strategy : FetchStrategy.NetworkOnly;
        return this.request<T>(eventName, otelEventName, postRequestConfig, postStrategy, onUploadProgress, onDownloadProgress, cancellationToken, timeoutInMs);
    }

    /**
     * Runs and instruments the result of execution of Http PUT operation.
     *
     * @param eventName Name of the instrumentation event
     * @param otelEventName Name of the OTEL instrumentation event.
     * @param url Absolute or relative URL of the resource that is being created or modified
     * @param data Data to be sent as the request message data
     * @param requestConfig The object which provide configuration for Http PUT
     * @param onUploadProgress A callback to receive upload progress events.
     * @param onDownloadProgress A callback to receive download progress events.
     * @param cancellationToken A cancellation token used to cancel the request.
     * @param timeoutInMs Maximum time to wait on the response.
     * @returns Promise object that will be completed asynchronously when Http PUT is done
     * and its result is instrumented.
     */
    public put<T>(
        eventName: KnownInstrumentationEvents,
        otelEventName: KnownRequestInstrumentationEvents,
        url: string,
        data: any,
        requestConfig: HttpRequestOptions = {},
        onUploadProgress?: (progressEvent: HttpProgressEvent) => void,
        onDownloadProgress?: (progressEvent: HttpProgressEvent) => void,
        cancellationToken?: PromiseCancellationToken,
        timeoutInMs?: number): InstrumentedHttpPromise<T> {

        const putRequestConfig: HttpRequestOptions = { ...requestConfig };
        putRequestConfig.url = url;
        putRequestConfig.method = HttpRequestMethod.Put;
        putRequestConfig.body = data;
        return this.request<T>(eventName, otelEventName, putRequestConfig, FetchStrategy.NetworkOnly, onUploadProgress, onDownloadProgress, cancellationToken, timeoutInMs);
    }

    /**
     * Runs and instruments the result of execution of HTTP DELETE operation.
     *
     * @param eventName Name of the instrumentation event
     * @param otelEventName Name of the OTEL instrumentation event.
     * @param url Absolute or relative URL of the resource that is being requested
     * @param data Data to be sent as the request message data
     * @param requestConfig The object which provide configuration for HTTP DELETE
     * @param cancellationToken A cancellation token used to cancel the request.
     * @param timeoutInMs Maximum time to wait on the response.
     * If omitted then metrics will be recorded.
     * @returns Promise object that will be completed asynchronously when HTTP DELETE is done
     * and its result is instrumented.
     */
    public delete<T>(
        eventName: KnownInstrumentationEvents,
        otelEventName: KnownRequestInstrumentationEvents,
        url: string,
        data: any,
        requestConfig: HttpRequestOptions = {},
        cancellationToken?: PromiseCancellationToken,
        timeoutInMs?: number): InstrumentedHttpPromise<T> {

        const deleteRequestConfig: HttpRequestOptions = { ...requestConfig };
        deleteRequestConfig.url = url;
        deleteRequestConfig.method = HttpRequestMethod.Delete;
        deleteRequestConfig.body = data;

        return this.request<T>(eventName, otelEventName, deleteRequestConfig, FetchStrategy.NetworkOnly, null, null, cancellationToken, timeoutInMs);
    }

    /**
     * Runs and instruments the result of execution of Http PATCH operation.
     *
     * @param eventName Name of the instrumentation event
     * @param otelEventName Name of the OTEL instrumentation event.
     * @param url Absolute or relative URL of the resource that is being created or modified
     * @param data Data to be sent as the request message data
     * @param requestConfig The object which provide configuration for Http PATCH
     * @param onUploadProgress A callback to receive upload progress events.
     * @param onDownloadProgress A callback to receive download progress events.
     * @param cancellationToken A cancellation token used to cancel the request.
     * @param timeoutInMs Maximum time to wait on the response.
     * @returns Promise object that will be completed asynchronously when Http PATCH is done
     * and its result is instrumented.
     */
    public patch<T>(
        eventName: KnownInstrumentationEvents,
        otelEventName: KnownRequestInstrumentationEvents,
        url: string,
        data: any,
        requestConfig: HttpRequestOptions = {},
        onUploadProgress?: (progressEvent: HttpProgressEvent) => void,
        onDownloadProgress?: (progressEvent: HttpProgressEvent) => void,
        cancellationToken?: PromiseCancellationToken,
        timeoutInMs?: number): InstrumentedHttpPromise<T> {

        const patchRequestConfig: HttpRequestOptions = { ...requestConfig };
        patchRequestConfig.url = url;
        patchRequestConfig.method = HttpRequestMethod.Patch;
        patchRequestConfig.body = data;
        return this.request<T>(eventName, otelEventName, patchRequestConfig, FetchStrategy.NetworkOnly, onUploadProgress, onDownloadProgress, cancellationToken, timeoutInMs);
    }

    private static failedResponseWithCode<T>(
        httpCode: HttpStatusCode,
        config: HttpRequestOptions,
        fromCache,
        error?: string
    ): InstrumentedHttpDataResponse<T> {
        return {
            data: null,
            status: httpCode,
            headers: new HttpHeaders(),
            config,
            ok: false,
            statusText: error || HttpStatusCode[httpCode],
            fromCache
        };
    }

    private prepareRequest(requestConfig: HttpRequestOptions, strategy: FetchStrategy): void {
        // Append header which tells Service Worker which fetch strategy to use.
        // IMPORTANT: Ensure that this is set only when Server Worker is active and not SCD'd off. Service Worker will
        // remove this header before sending request via network because cross-domain requests may not
        // accept custom headers. This header removal code runs within Service Worker thread so we have
        // to make sure that header is not added when Server Worker is inactive.
        if (this.serviceWorkerStateService.currentState === ServiceWorkerState.Controlled && this.serviceWorkerConfigService.config.enabled) {
            if (!requestConfig.headers) {
                requestConfig.headers = new Map<string, string | Array<string>>();
            } else {
                // Service worker throws on undefined header values
                requestConfig.headers.forEach((v, k) => {
                    if (typeof v === 'undefined') {
                        requestConfig.headers.set(k, '');
                        const params: InstrumentationParams = {};
                        params[InstrumentationParam.ItemCategory] = 'Malformed Request';
                        params[InstrumentationParam.Error] = `Undefined header passed to Service Worker: ${k}`;
                        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.UnhandledException, params);
                    }
                });
            }

            requestConfig.headers.set(serviceWorkerFetchStrategyHeader, strategy);
        }

        // Append headers and add query param for flight overrides to requests going to OfficeHome endpoint
        // IMPORTANT: Don't use the .includes() function to check for the OfficeHome endpoint as some requests to other services
        // may have officeHomeApiRoot value somewhere in the URL.
        // IMPORTANT: Check against value with trailing slash added to account for the case when officeHomeApiRoot is empty string
        // indicating that API root is on path root.
        const officeHomeApiRootWithTrailingSlash = `${this.contextService.sharedContext.officeHomeApiRoot}/`;
        if (requestConfig.url.lastIndexOf(officeHomeApiRootWithTrailingSlash, 0) === 0) {
            if (!requestConfig.headers) {
                requestConfig.headers = new Map<string, string | Array<string>>();
            }

            const apiConfig = this.officeHomeApiConfig;
            const startupConfig = this.startupConfigService.config;
            apiConfig.correlationIdHeader && startupConfig.sharedClientStartupContext.correlationId && requestConfig.headers.set(apiConfig.correlationIdHeader, startupConfig.sharedClientStartupContext.correlationId);
            apiConfig.userIdHeader && startupConfig.puid && requestConfig.headers.set(apiConfig.userIdHeader, startupConfig.puid);
            apiConfig.tenantIdHeader && startupConfig.tenantId && requestConfig.headers.set(apiConfig.tenantIdHeader, startupConfig.tenantId);
            apiConfig.authTypeHeader && startupConfig.authTypeString && requestConfig.headers.set(apiConfig.authTypeHeader, startupConfig.authTypeString);
            apiConfig.authVersionHeader && startupConfig.authVersion && requestConfig.headers.set(apiConfig.authVersionHeader, startupConfig.authVersion);

            if (!requestConfig.params) {
                requestConfig.params = {};
            }

            if (apiConfig.workloadQueryParameter) {
                requestConfig.params[apiConfig.workloadQueryParameter] = this.workloadQueryParameterValue;
            }

            if (startupConfig.sharedClientStartupContext.testTraffic && apiConfig.testTrafficHeader) {
                requestConfig.headers.set(apiConfig.testTrafficHeader, '1');
            }

            if (!startupConfig.sharedClientStartupContext.corpNet && apiConfig.externalTrafficHeader) {
                requestConfig.headers.set(apiConfig.externalTrafficHeader, '1');
            }

            if ((apiConfig.flightOverridesParamName && apiConfig.flightOverrides) ||
                (apiConfig.featureOverridesParamName && apiConfig.featureOverrides)) {
                if (apiConfig.flightOverridesParamName &&
                    apiConfig.flightOverrides &&
                    !requestConfig.params.hasOwnProperty(apiConfig.flightOverridesParamName)) {
                    requestConfig.params[apiConfig.flightOverridesParamName] = apiConfig.flightOverrides;
                }

                if (apiConfig.featureOverridesParamName &&
                    apiConfig.featureOverrides &&
                    !requestConfig.params.hasOwnProperty(apiConfig.featureOverridesParamName)) {
                    requestConfig.params[apiConfig.featureOverridesParamName] = apiConfig.featureOverrides;
                }
            }
        }
    }
}
